import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api from "../lib/api";
import DocumentsList from "../components/DocumentsList";
import { WORKER_DOC_CATEGORIES, fmtDate } from "../lib/utils-app";
import { ArrowLeft, User, UserCheck, UserX, Trash2 } from "lucide-react";
import { WorkerMissingPanel } from "../components/MissingDocs";
import { WorkerStatoBadge } from "./WorkersList";
import { useAuth } from "../context/AuthContext";
import { canManage } from "../lib/utils-app";
import { toast } from "sonner";

export default function WorkerDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [w, setW] = useState(null);
  const [company, setCompany] = useState(null);
  const [mk, setMk] = useState(0);

  const removeWorker = async () => {
    const typed = window.prompt(`ATTENZIONE: verranno eliminati il lavoratore ${w.cognome} ${w.nome} e TUTTI i suoi documenti in modo definitivo.\n\nPer confermare scrivi ELIMINA:`);
    if (typed !== "ELIMINA") { if (typed !== null) toast.info("Eliminazione annullata"); return; }
    try {
      const { data } = await api.delete(`/workers/${w.id}`);
      toast.success(`Lavoratore eliminato (${data.documents_deleted} documenti rimossi)`);
      nav(`/aziende/${w.company_id}`, { replace: true });
    } catch { toast.error("Errore eliminazione"); }
  };

  const toggleStato = async () => {
    const stato = w.stato === "cessato" ? "attivo" : "cessato";
    if (!window.confirm(stato === "cessato" ? "Segnare il lavoratore come CESSATO? Verrà escluso dai controlli sui documenti mancanti e dai riepiloghi." : "Riattivare il lavoratore?")) return;
    const body = { ...w, stato, data_cessazione: stato === "cessato" ? (w.data_cessazione || new Date().toISOString().slice(0, 10)) : "" };
    try { const { data } = await api.put(`/workers/${w.id}`, body); setW(data); setMk(k => k + 1); toast.success(`Lavoratore ${stato}`); }
    catch { toast.error("Errore aggiornamento stato"); }
  };

  useEffect(() => {
    (async () => {
      const { data } = await api.get(`/workers/${id}`);
      setW(data);
      const c = await api.get(`/companies/${data.company_id}`);
      setCompany(c.data);
    })();
  }, [id]);

  if (!w) return <div className="text-slate-500">Caricamento…</div>;

  return (
    <div className="space-y-6">
      <div>
        <Link to={`/aziende/${w.company_id}`} className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900">
          <ArrowLeft size={14} /> {company?.ragione_sociale || "Azienda"}
        </Link>
      </div>

      <header className="bg-white border border-slate-200 rounded-lg p-6 flex flex-wrap items-start gap-4">
        <div className="w-14 h-14 rounded-lg bg-blue-50 text-blue-800 flex items-center justify-center"><User size={26} /></div>
        <div className="flex-1 min-w-[200px]">
          <div className="text-xs font-mono uppercase tracking-widest text-blue-700 mb-1">Lavoratore</div>
          <h1 className="font-display text-2xl font-bold text-slate-900 flex items-center gap-3">{w.cognome} {w.nome} <WorkerStatoBadge stato={w.stato} /></h1>
          <div className="text-sm text-slate-500 font-mono mt-1">{w.codice_fiscale || "—"}</div>
        </div>
        {canManage(user) && (
          <div className="flex flex-wrap gap-2">
            <button onClick={toggleStato} data-testid="worker-stato-toggle"
              className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm border ${w.stato === "cessato" ? "border-emerald-300 text-emerald-800 hover:bg-emerald-50" : "border-slate-300 text-slate-700 hover:bg-slate-50"}`}>
              {w.stato === "cessato" ? <><UserCheck size={16} /> Riattiva lavoratore</> : <><UserX size={16} /> Segna come cessato</>}
            </button>
            <button onClick={removeWorker} data-testid="worker-delete"
              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm border border-red-300 text-red-700 hover:bg-red-50">
              <Trash2 size={16} /> Elimina lavoratore
            </button>
          </div>
        )}
      </header>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <InfoCard label="Mansione" value={w.mansione} />
        <InfoCard label="Data nascita" value={fmtDate(w.data_nascita)} />
        <InfoCard label="Data assunzione" value={fmtDate(w.data_assunzione)} />
        <InfoCard label="Data cessazione" value={fmtDate(w.data_cessazione)} />
      </div>

      <WorkerMissingPanel workerId={w.id} reloadKey={mk} />

      <section>
        <h2 className="font-display font-semibold text-slate-900 mb-3">Documenti del lavoratore</h2>
        <DocumentsList entityType="worker" entityId={w.id} companyId={w.company_id} categories={WORKER_DOC_CATEGORIES} onChanged={() => setMk(k => k + 1)} />
      </section>
    </div>
  );
}

function InfoCard({ label, value }) {
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-4">
      <div className="text-xs uppercase tracking-wider text-slate-500 font-mono">{label}</div>
      <div className="mt-1 text-slate-900">{value || "—"}</div>
    </div>
  );
}
